import axios, { type Method } from "axios";
import { app } from "./firebase";
import { getAuth, type User as FirebaseUser } from "firebase/auth";
import { v4 as uuidv4 } from "uuid";

export interface RestRequest {
  method: Method;
  endpoint: string;
  params?: Record<string, unknown>;
  body?: unknown;
  /** When true (default) attaches the Firebase JWT as a Bearer token. */
  auth?: boolean;
}

/**
 * Central HTTP gateway. All backend communication flows through `sendRequest`.
 * The public surface (method names + behavior) is intentionally preserved.
 */
class RestGate {
  private readonly serverURL: string;
  readonly id: string;
  /** Set during a login/register flow before `getAuth().currentUser` is ready. */
  tentativeFirebaseUser: FirebaseUser | null = null;

  constructor() {
    this.serverURL = process.env.REACT_APP_BACKEND_URL ?? "";
    console.log(`this.serverURL = ${this.serverURL}`);
    this.id = uuidv4();
  }

  private printError(message: string): void {
    console.error(message);
  }

  getJwtToken = async (): Promise<string> => {
    const currentUser = getAuth(app).currentUser;
    if (this.tentativeFirebaseUser) {
      return this.tentativeFirebaseUser.getIdToken();
    }
    if (currentUser) {
      return currentUser.getIdToken();
    }
    this.printError("User is not authenticated");
    throw new Error("User is not authenticated");
  };

  async getHeaders(): Promise<Record<string, string>> {
    const jwtToken = await this.getJwtToken();
    return {
      Authorization: `Bearer ${jwtToken}`,
      "Content-Type": "application/json",
    };
  }

  async sendRequest<T = any>({
    method,
    endpoint,
    params = {},
    body = {},
    auth = true,
  }: RestRequest): Promise<T> {
    try {
      const url = `${this.serverURL}${endpoint}`;
      const response = await axios<T>({
        method,
        url,
        ...(auth ? { headers: await this.getHeaders() } : {}),
        params,
        data: body,
      });
      return response.data;
    } catch (error) {
      const cause =
        (axios.isAxiosError(error) && error.response?.data?.error) ||
        "Server error occurred";
      throw new Error(cause);
    }
  }

  async sendBeacon(body: unknown): Promise<void> {
    const url = `${this.serverURL}/beacon`;
    try {
      await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
        keepalive: true,
      });
    } catch (err) {
      console.error("Beacon fetch failed:", err);
    }
  }
}

const restGate = new RestGate();
export default restGate;
